import { X, ArrowRight, Check, XCircle, Clock, AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import PlayingCard from "./PlayingCard";

// Trade status badge
function TradeStatusBadge({ status }) {
  const statusConfig = {
    pending: { label: "未承認", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
    accepted: { label: "成立", color: "bg-green-500/20 text-green-400", icon: Check },
    rejected: { label: "却下", color: "bg-red-500/20 text-red-400", icon: XCircle },
    conflict: { label: "競合", color: "bg-orange-500/20 text-orange-400", icon: AlertCircle },
    cancelled: { label: "取下げ", color: "bg-gray-500/20 text-gray-400", icon: RotateCcw },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

// Single trade entry
function TradeEntry({ trade, players }) {
  const fromPlayer = players.find(p => p.id === trade.from_player_id);
  const toPlayer = players.find(p => p.id === trade.to_player_id);

  // Use the pre-computed card data from backend
  const offerCards = trade.offer_cards_data || [];
  const requestCards = trade.request_cards_data || [];

  return (
    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
      {/* Header: Players and Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{fromPlayer?.nickname || "不明"}</span>
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-foreground">{toPlayer?.nickname || "不明"}</span>
        </div>
        <TradeStatusBadge status={trade.status} />
      </div>

      {/* Trade Content */}
      <div className="flex items-center gap-3">
        {/* Offer (from player gives) */}
        <div className="flex-1 space-y-1">
          <div className="text-xs text-muted-foreground">提供</div>
          <div className="flex items-center gap-1 flex-wrap">
            {offerCards.length > 0 && (
              <div className="flex gap-0.5">
                {offerCards.map((card, i) => (
                  <div key={i} className="w-6 h-9">
                    <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="xs" />
                  </div>
                ))}
              </div>
            )}
            {trade.offer_chips > 0 && (
              <span className="text-xs bg-brass/20 text-brass px-1.5 py-0.5 rounded">
                {trade.offer_chips}チップ
              </span>
            )}
            {offerCards.length === 0 && trade.offer_chips === 0 && (
              <span className="text-xs text-muted-foreground">なし</span>
            )}
          </div>
        </div>

        {/* Arrow */}
        <div className="text-muted-foreground">
          <ArrowRight className="w-4 h-4" />
        </div>

        {/* Request (from player wants) */}
        <div className="flex-1 space-y-1">
          <div className="text-xs text-muted-foreground">要求</div>
          <div className="flex items-center gap-1 flex-wrap">
            {requestCards.length > 0 && (
              <div className="flex gap-0.5">
                {requestCards.map((card, i) => (
                  <div key={i} className="w-6 h-9">
                    <PlayingCard suit={card.suit} rank={card.rank} isPublic={true} size="xs" />
                  </div>
                ))}
              </div>
            )}
            {trade.request_chips > 0 && (
              <span className="text-xs bg-brass/20 text-brass px-1.5 py-0.5 rounded">
                {trade.request_chips}チップ
              </span>
            )}
            {requestCards.length === 0 && trade.request_chips === 0 && (
              <span className="text-xs text-muted-foreground">なし</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TradeLogModal({ isOpen, onClose, trades, players }) {
  if (!isOpen) return null;

  // Sort trades by timestamp (newest first)
  const sortedTrades = [...(trades || [])].sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  // Group by status for summary
  const statusCounts = {
    accepted: sortedTrades.filter(t => t.status === "accepted").length,
    pending: sortedTrades.filter(t => t.status === "pending").length,
    rejected: sortedTrades.filter(t => t.status === "rejected").length,
    cancelled: sortedTrades.filter(t => t.status === "cancelled").length,
    conflict: sortedTrades.filter(t => t.status === "conflict").length,
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div 
        className="bg-card border border-brass/30 rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <h2 className="font-display text-lg text-foreground">取引提案ログ</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground h-8 w-8"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Summary - Fixed */}
        <div className="px-4 py-2 border-b border-border shrink-0">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="text-muted-foreground">合計 {sortedTrades.length}件:</span>
            {statusCounts.accepted > 0 && (
              <span className="text-green-400">成立 {statusCounts.accepted}</span>
            )}
            {statusCounts.pending > 0 && (
              <span className="text-yellow-400">未承認 {statusCounts.pending}</span>
            )}
            {statusCounts.rejected > 0 && (
              <span className="text-red-400">却下 {statusCounts.rejected}</span>
            )}
            {statusCounts.cancelled > 0 && (
              <span className="text-gray-400">取下げ {statusCounts.cancelled}</span>
            )}
            {statusCounts.conflict > 0 && (
              <span className="text-orange-400">競合 {statusCounts.conflict}</span>
            )}
          </div>
        </div>

        {/* Trade List - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 overscroll-contain">
          {sortedTrades.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              このラウンドではまだ取引提案がありません
            </div>
          ) : (
            sortedTrades.map((trade) => (
              <TradeEntry 
                key={trade.id} 
                trade={trade} 
                players={players}
              />
            ))
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="p-4 border-t border-border shrink-0">
          <Button
            onClick={onClose}
            className="w-full bg-brass text-accent-foreground hover:bg-brass/90"
          >
            閉じる
          </Button>
        </div>
      </div>
    </div>
  );
}
